import type { Answer, Generated, GeneratedType, Question, QuestionStatus, Section } from "../data"

// ============================================================
// QUESTION SHAPE VALIDATOR
// Throws on the first failure with a path to the offending field.
// ============================================================

const STATUSES: readonly QuestionStatus[] = ["unverified", "verified", "discarded"]
const GENERATED_TYPES: readonly GeneratedType[] = ["alt-answers", "paired"]
const SECTIONS: readonly Section[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

class QuestionValidationError extends Error {
  constructor(path: string, detail: string) {
    super(`${path}: ${detail}`)
    this.name = "QuestionValidationError"
  }
}

function fail(path: string, detail: string): never {
  throw new QuestionValidationError(path, detail)
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v)
}

function checkString(path: string, v: unknown): string {
  if (typeof v !== "string" || v.length === 0) {
    fail(path, `expected non-empty string, got ${JSON.stringify(v)}`)
  }
  return v
}

function checkPositiveInt(path: string, v: unknown): number {
  if (typeof v !== "number" || !Number.isInteger(v) || v < 1) {
    fail(path, `expected positive integer, got ${JSON.stringify(v)}`)
  }
  return v
}

function checkDate(path: string, v: unknown): string {
  const s = checkString(path, v)
  if (!DATE_RE.test(s)) {
    fail(path, `expected YYYY-MM-DD, got ${JSON.stringify(s)}`)
  }
  return s
}

function checkEnum<T extends string>(path: string, v: unknown, allowed: readonly T[]): T {
  if (typeof v !== "string" || !(allowed as readonly string[]).includes(v)) {
    fail(path, `expected one of ${allowed.join(", ")}, got ${JSON.stringify(v)}`)
  }
  return v as T
}

function checkSection(path: string, v: unknown): Section {
  if (!(SECTIONS as readonly number[]).includes(v as number)) {
    fail(path, `expected section 1..10, got ${JSON.stringify(v)}`)
  }
  return v as Section
}

function checkOtherChoices(path: string, v: unknown): string[] {
  if (!Array.isArray(v) || v.length === 0) {
    fail(path, `expected non-empty array, got ${JSON.stringify(v)}`)
  }
  return v.map((s, i) => checkString(`${path}[${i}]`, s))
}

function checkAnswer(path: string, v: unknown): Answer {
  if (!isPlainObject(v)) fail(path, `expected object, got ${JSON.stringify(v)}`)
  return {
    text: checkString(`${path}.text`, v.text),
    section: checkSection(`${path}.section`, v.section),
    topic: checkString(`${path}.topic`, v.topic),
    page: checkPositiveInt(`${path}.page`, v.page),
  }
}

function checkGenerated(path: string, v: unknown): Generated | null {
  if (v === null) return null
  if (!isPlainObject(v)) fail(path, `expected object or null, got ${JSON.stringify(v)}`)
  return {
    parent: checkString(`${path}.parent`, v.parent),
    type: checkEnum(`${path}.type`, v.type, GENERATED_TYPES),
    rationale: checkString(`${path}.rationale`, v.rationale),
  }
}

function checkQuestion(path: string, v: unknown): Question {
  if (!isPlainObject(v)) fail(path, `expected object, got ${JSON.stringify(v)}`)
  return {
    id: checkString(`${path}.id`, v.id),
    q: checkString(`${path}.q`, v.q),
    answer: checkAnswer(`${path}.answer`, v.answer),
    otherChoices: checkOtherChoices(`${path}.otherChoices`, v.otherChoices),
    generated: checkGenerated(`${path}.generated`, v.generated),
    batch: checkString(`${path}.batch`, v.batch),
    version: checkPositiveInt(`${path}.version`, v.version),
    updatedAt: checkDate(`${path}.updatedAt`, v.updatedAt),
    status: checkEnum(`${path}.status`, v.status, STATUSES),
  }
}

export function validateQuestions(items: unknown): Question[] {
  if (!Array.isArray(items)) {
    throw new QuestionValidationError("questions", `expected array, got ${JSON.stringify(items)}`)
  }
  const result: Question[] = []
  const seen = new Map<string, number>()
  for (let i = 0; i < items.length; i++) {
    const q = checkQuestion(`questions[${i}]`, items[i])
    const prior = seen.get(q.id)
    if (prior !== undefined) {
      throw new QuestionValidationError(
        `questions[${i}].id`,
        `duplicate identifier ${JSON.stringify(q.id)}, first seen at questions[${prior}]`,
      )
    }
    seen.set(q.id, i)
    result.push(q)
  }
  return result
}
